'use client';

/**
 * ProjectConfigurationTab
 *
 * Configuration tab content for project page.
 * Shows the effective config from .cortex/config.json with sync controls.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Inline,
  Stack,
  Text,
} from '@cortex/ui';
import { AlertCircle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import { ConfigDisplay } from './ConfigDisplay';
import { ConfigSyncStatus } from './ConfigSyncStatus';

interface Project {
  id: string;
  name: string;
  repo_url: string | null;
  config: Record<string, unknown>;
  config_raw: Record<string, unknown> | null;
  config_sync_status: string;
  config_synced_at: string | null;
  config_sync_error: string | null;
  config_file_sha: string | null;
}

interface ProjectConfigurationTabProps {
  project: Project;
  onUpdate: () => void;
}

export function ProjectConfigurationTab({
  project,
  onUpdate,
}: ProjectConfigurationTabProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Sync config from repository
  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetchWithAuth(
        `/admin/projects/${project.id}/config/sync`,
        {
          method: 'POST',
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to sync configuration');
      }

      // Check sync result status
      if (data.data.status === 'synced') {
        setSuccess('Configuration synced successfully');
      } else if (data.data.status === 'not_found') {
        // Show debug info if available
        const debugInfo = data.data.error ? ` (${data.data.error})` : '';
        setSuccess(`No .cortex/config.json found - using defaults${debugInfo}`);
      } else if (data.data.status === 'invalid') {
        setError(`Configuration is invalid: ${data.data.error}`);
      } else if (data.data.status === 'error') {
        setError(`Sync failed: ${data.data.error}`);
      }

      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);

      onUpdate();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to sync configuration'
      );
    } finally {
      setSyncing(false);
    }
  };

  // Format synced at timestamp
  const formatSyncedAt = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Check if project can be synced
  const canSync = !!project.repo_url;

  return (
    <Stack gap="lg">
      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Sync Status Header */}
      <Inline justify="between">
        <Inline gap="md">
          <ConfigSyncStatus status={project.config_sync_status} />
          <Text color="muted" size="sm">
            Last synced: {formatSyncedAt(project.config_synced_at)}
          </Text>
        </Inline>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={!canSync}
          loading={syncing}
        >
          {!syncing && <RefreshCw className="h-4 w-4" />}
          Sync Now
        </Button>
      </Inline>

      {/* No repository warning */}
      {!project.repo_url && (
        <Alert variant="warning">
          <AlertCircle className="h-5 w-5" />
          <AlertDescription>
            <Text weight="medium" size="sm">
              No repository configured
            </Text>
            <Text color="muted" size="sm" className="mt-1">
              Add a repository URL in the Details tab to enable configuration
              sync.
            </Text>
          </AlertDescription>
        </Alert>
      )}

      {/* Sync error details */}
      {project.config_sync_error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            <Text weight="medium" size="sm">
              Last sync failed
            </Text>
            <Text color="muted" size="sm" className="mt-1">
              {project.config_sync_error}
            </Text>
          </AlertDescription>
        </Alert>
      )}

      {/* Configuration display */}
      <Stack gap="md">
        <Inline justify="between" className="mb-3">
          <Text weight="medium">Effective Configuration</Text>
          {project.config_file_sha && (
            <Text color="muted" size="xs">
              SHA: {project.config_file_sha.substring(0, 7)}
            </Text>
          )}
        </Inline>
        <Text color="muted" size="sm" className="mb-4">
          This is the merged configuration with defaults applied. It represents
          what Cortex uses when processing requests for this project.
        </Text>
        <ConfigDisplay config={project.config} />
      </Stack>

      {/* Raw config (if different from effective) */}
      {project.config_raw && Object.keys(project.config_raw).length > 0 && (
        <Stack gap="md">
          <Text weight="medium" className="mb-3">
            Raw Configuration
          </Text>
          <Text color="muted" size="sm" className="mb-4">
            The actual contents of .cortex/config.json before defaults are
            applied.
          </Text>
          <ConfigDisplay config={project.config_raw} />
        </Stack>
      )}
    </Stack>
  );
}
